import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { auth } from "@/lib/api";
import { User, LogOut, KeyRound, ChevronDown } from "lucide-react";

/**
 * Sticky top bar with a brand-color avatar dropdown.
 * Always shows the current user's initial + Logout — solves the "Logout
 * not clearly visible" feedback. Mounted in both AdminLayout and the
 * employee portal pages.
 */
export default function TopBar({ extra = null }) {
  const { branding, user } = useBranding();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const brand = branding?.theme_color || "#0EA5E9";
  const name = user?.name || "Account";
  const initial = (name || "U").trim()[0]?.toUpperCase() || "U";
  const role = user?.role || "";

  function logout() {
    auth.clear();
    navigate("/login");
  }

  return (
    <div className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200" data-testid="topbar">
      <div className="flex items-center justify-end gap-2 px-4 sm:px-6 lg:px-10 h-14">
        {extra}
        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50 transition"
            data-testid="topbar-avatar"
          >
            <span
              className="h-8 w-8 rounded-full grid place-items-center text-white font-bold text-sm shadow-sm"
              style={{ background: brand }}
            >
              {initial}
            </span>
            <span className="hidden sm:flex flex-col items-start leading-tight">
              <span className="text-xs font-bold text-slate-900 max-w-[140px] truncate">{name}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{role.replace("_", " ")}</span>
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden" data-testid="topbar-menu">
              <Link
                to="/profile"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm hover:bg-slate-50 text-slate-700"
                data-testid="topbar-profile-link"
              >
                <User className="h-4 w-4 text-slate-500" /> My Profile
              </Link>
              <Link
                to="/profile?tab=password"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm hover:bg-slate-50 text-slate-700"
                data-testid="topbar-password-link"
              >
                <KeyRound className="h-4 w-4 text-slate-500" /> Change Password
              </Link>
              <button
                onClick={logout}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm hover:bg-red-50 text-red-600 border-t border-slate-100"
                data-testid="topbar-logout"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
